<script setup lang="ts">
// Card de recompensa da tela Recompensas (Minha Conta V2, ENG-362). Figma
// nodes 18223:30087/18327:32996 — mesmos dados do card antigo (components/
// user/rewards/Item.vue: useRewards/parseRewardDescription/getRewardStatus),
// reskin nos tokens v2-* em vez do layout de lista antigo.
import {
  parseRewardDescription,
  getRewardStatusV2,
  isRewardAppOnlyBlocked,
} from '@/utils/rewards';

type Props = {
  reward: Reward;
  // aba "Disponíveis": mostra countdown + botão de resgate.
  // aba "Finalizados": mostra a pill de status (Resgatado/Expirado/...).
  redeemable: boolean;
};

const { reward, redeemable } = defineProps<Props>();

const emit = defineEmits<{ redeem: [] }>();

const { showMoneyFormat } = useWallet();
const { normalizeThumbnail, getThumbnailPathSet } = useCasino();
// v-if (não só CSS hidden/flex) pro prazo+ação não ficar montado duas vezes —
// WidgetsCountdown roda um setInterval; duas instâncias por card é
// desperdício. isLayoutMobileOrTablet (<1024px) pra bater com o corte lg: do
// template — abaixo de 1024 já é a versão mobile deste card.
const { isLayoutMobileOrTablet: isMobile } = useLayout();

// sem imagem de jogo (reward.game_image vazio), cai sempre no cifrão.
const FALLBACK_IMAGE = '/images/user-v2/rewards/reward-dollar.png';

const thumbnailPath = computed(() =>
  reward.game_image ? `${normalizeThumbnail(reward.game_image)}/public` : '',
);

const thumbnailPathSet = computed(() =>
  reward.game_image ? getThumbnailPathSet(reward.game_image) : '',
);

const { value: extractedValue, description } = parseRewardDescription(
  reward.player_desc,
);

const countDownTotalTime = computed(() => {
  const diff = new Date(reward.expires_at).getTime() - Date.now();

  return diff > 0 ? diff / 1000 : 0;
});

const isCountdownFinished = ref(false);

const isExpired = computed(
  () => isCountdownFinished.value || new Date(reward.expires_at) < new Date(),
);

const status = computed(() =>
  getRewardStatusV2(reward.status, !!reward.is_app_only),
);

// ENG-799: bônus só resgatável pelo app, aberto fora do app. Expirado ganha:
// não faz sentido mandar baixar o app por um prêmio morto.
const isAppOnlyBlocked = computed(
  () => redeemable && !isExpired.value && isRewardAppOnlyBlocked(reward),
);

// ENG-799: DENTRO do app o bônus app-only é resgatável, mas ganha o MESMO
// card do Figma com a mensagem adaptada — quem já tem o app não precisa de
// convite pra baixar. Só na aba de disponíveis: em Finalizados quem comunica
// isso é a pill "Resgatado pelo app".
const isAppOnlyInsideApp = computed(
  () => redeemable && !!reward.is_app_only && !isAppOnlyBlocked.value,
);
</script>

<template>
  <!-- ENG-799: o bônus app-only troca o card INTEIRO pelo desenho do Figma
       (node 23956:298146), não é um estado deste aqui. Fora do app a variante
       convida a baixar; dentro dele é a mesma arte com a mensagem trocada e o
       resgate liberado. -->
  <UserV2RewardCardAppOnly
    v-if="isAppOnlyBlocked || isAppOnlyInsideApp"
    :reward="reward"
    :variant="isAppOnlyBlocked ? 'download' : 'exclusive'"
    @redeem="emit('redeem')"
  />

  <div
    v-else
    class="border-solid flex min-w-0 flex-col gap-[10px] rounded-[9px] border-l-[5px] border-v2-primary bg-v2-primary/10 px-[16px] py-[10px] lg:flex-row lg:items-center lg:gap-[18px]"
  >
    <!-- ícone + título + valor/quantidade numa linha só. lg:contents no
         mobile some com a caixa desta div (os filhos viram direto do flex
         row de cima) — no desktop ela nem existe, layout original intacto. -->
    <div class="flex min-w-0 items-center gap-[12px] lg:contents">
      <img
        :alt="description"
        class="size-[48px] shrink-0 rounded-[11px] object-cover lg:size-[75px]"
        :src="thumbnailPath || getAssetsUrlForCDN(FALLBACK_IMAGE)"
        :srcset="thumbnailPathSet || undefined"
      />

      <!-- coluna de texto. desktop: título (cresce) + prazo + ação
           enfileirados na mesma linha, valor/quantidade sozinho embaixo.
           mobile: só título + valor/quantidade — prazo+ação vira uma linha
           própria fora daqui, embaixo do ícone (ver abaixo). -->
      <div class="flex min-w-0 flex-1 flex-col gap-[2px] lg:gap-0">
        <div class="flex items-start justify-between gap-[10px]">
          <p
            class="min-w-0 truncate text-[14px] font-semibold leading-[20px] text-white"
          >
            {{ description }}
          </p>

          <!-- desktop: prazo + ação inline com o título -->
          <div
            v-if="!isMobile"
            class="flex shrink-0 items-start gap-[10px]"
          >
            <UserV2RewardCardActions
              :count-down-total-time="countDownTotalTime"
              :is-expired="isExpired"
              :redeemable="redeemable"
              :status="status"
              :updated-at="reward.updated_at"
              @redeem="emit('redeem')"
              @timer-finished="isCountdownFinished = true"
            />
          </div>
        </div>

        <div
          class="flex items-center gap-[10px] text-[11px] text-white/50 lg:-mt-[14px]"
        >
          <span v-if="extractedValue">Valor: {{ extractedValue }}</span>
          <span v-else-if="reward.amount">
            Valor: {{ showMoneyFormat({ value: reward.amount / 100 }) }}
          </span>

          <span
            v-if="(extractedValue || reward.amount) && reward.quantity"
            class="h-[13px] w-px bg-white/20"
          />

          <span v-if="reward.quantity">Quantidade: {{ reward.quantity }}</span>
        </div>
      </div>
    </div>

    <!-- mobile: prazo + ação, sozinhos, alinhados à esquerda (embaixo do
         ícone) — fora da coluna de texto, ocupa a largura do card inteiro. -->
    <div
      v-if="isMobile"
      class="flex items-center gap-[10px]"
    >
      <UserV2RewardCardActions
        :count-down-total-time="countDownTotalTime"
        :is-expired="isExpired"
        :redeemable="redeemable"
        :status="status"
        :updated-at="reward.updated_at"
        @redeem="emit('redeem')"
        @timer-finished="isCountdownFinished = true"
      />
    </div>
  </div>
</template>
